
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { isValid, parse, getYear, getMonth, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import VueUtils from '../../assets/utils/VueUtils';
import DateUtil from '../../assets/utils/DateUtil';
import Util from '../../assets/utils/Util';

@Component({
  name: 'TkDate',
  components: {
  },
})
class TkDate extends Vue{
  // #region [ PROPS ]
  @Prop({ default: false })
  private readonly cssStyle: string | boolean;

  @Prop({ default: false })
  private readonly dense: boolean;

  @Prop({ default: false })
  private readonly disabled: boolean;

  @Prop({ default: false })
  private readonly error: boolean;

  @Prop({ default: false })
  private readonly hideDetails: boolean;

  @Prop({ default: undefined })
  readonly hint: string;

  @Prop({ default: false })
  private readonly hideDatepicker: boolean | string;

  @Prop({ default: '' })
  private readonly label: string;

  @Prop({ default: null })
  private readonly max: string;

  @Prop({ default: null })
  private readonly min: string;

  @Prop({ default: false })
  private readonly month: string | boolean;

  @Prop({ default: false })
  private readonly outlined: boolean;

  @Prop({ default: false })
  readonly persistentHint: boolean;

  @Prop({ default: false })
  private readonly readonly: boolean;

  @Prop({ default: false })
  private readonly required: boolean;

  @Prop({ default: undefined })
  private readonly rules: (((value: any) => boolean) | boolean | string);

  @Prop({ default: true })
  private readonly showClear: boolean;

  @Prop({ default: true })
  private readonly showIcon: boolean;

  @Prop({ default: '' })
  private readonly value: string;

  @Prop({ default: false })
  private readonly validateOnBlur: string | boolean;

  @Prop({ default: false })
  private readonly multiple: boolean;

  @Prop({ default: false })
  private readonly reset: boolean;
  // #endregion

  // #region [ EVENTS ]
  @Emit('input')
  @Emit('updateSelectValue')
  private emitInput(value?: string) {
    return value || this.internalValue;
  }

  @Emit('focus')
  @Emit('updateSelectValue')
  private emitFocus(value?: string) {
    return value || this.internalValue;
  }
  // #endregion

  // #region [ DATA ]
  private readonly dateInsertMode = 'insertMode';
  private readonly dateViewMode = 'viewMode';
  private readonly defaultStyle = 'padding-top: 8px !important; margin-top: 8px !important;';

  private showMenu = false;
  private internalValue = '';
  private activator: any = null;
  private selectedDates = [];
  private isEventListenerAddedDay = false;
  private isEventListenerAddedMonth = false;
  private isEventListenerAddedYear = false;
  private isEventListenerAddedCurrentHeader = false;
  private timerCalendar = 350;

  // Flag que diz se a data foi escolhida pelo VDatePicker
  private dateWasSelected = false;

  private dateModes = {
    insertMode: {
      icon: '',
    },
    viewMode: {
      icon: 'mdi-close',
    },
  };
  // #endregion

  // #region [ COMPUTED ]
  private get dateInYYYYMMDD(): string {
    if (this.internalValue === '') {
      return '';
    }

    const date = DateUtil.formatToYYYYMMDD(this.validDate);

    return date;
  }

  private get dateMultipleInYYYYMMDD(): string {
    return '';
    if (this.internalValue === '') {
      return '';
    }

    const date = DateUtil.formatToYYYYMMDD(this.validDate);

    return date;
  }

  private get icon(): string {
    const showIcon = VueUtils.propertyIsTrue(this.showIcon);

    // Mostra icon para limpar campo quando em modo de visualização (uma data está/está sendo preenchida)
    if (this.showClear && this.dateMode === this.dateViewMode) {
      return this.dateModes.viewMode.icon;
    }

    const icon = showIcon ?
      'mdi-calendar' :
      undefined;

    return icon;
  }

  private get propMonth(): boolean {
    const month = VueUtils.propertyIsTrue(this.month);

    return month;
  }

  private get textReadOnly(): boolean {
    const readOnly = this.readonly || this.propMonth || this.multiple;

    return readOnly;
  }

  private get datePickerType(): string {
    const type = this.propMonth ?
      'month' :
      'date';

    return type;
  }

  private get dateMode() {
    const isDataSelected = this.internalValue;

    return isDataSelected ? this.dateViewMode : this.dateInsertMode;
  }

  private get validDate(): string {
    const adjustedDate = DateUtil.formatDate(this.internalValue);

    return adjustedDate;
  }

  private get changed(): boolean {
    return this.value !== this.internalValue;
  }

  private get style() {
    return this.cssStyle ? this.cssStyle : this.defaultStyle;
  }

  public get hideDatepickerProp(): boolean {
    return VueUtils.propertyIsTrue(this.hideDatepicker);
  }

  get _max(): string {
    const date = this.__convertDMYToISO(this.max);
    return date;
  }

  get _min(): string {
    const date = this.__convertDMYToISO(this.min);
    return date;
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private valueOnChange() {
    const date = this.value || '';

    try {

      this.selectedDates = date ? date.split(', ') : [];
      this.dateMultipleChange();
    } catch (error) {
      this.selectedDates = [];
    }

    this.internalValue = date;
  }

  @Watch('reset', { immediate: true })
  private resetOnChange(value: boolean) {

    if(value) {
      this.selectedDates = [];
      this.isEventListenerAddedDay = false;
      this.isEventListenerAddedMonth = false;
      this.isEventListenerAddedYear = false;
      this.isEventListenerAddedCurrentHeader = false;
      this.dateMultipleChange();
    }
  }
  // #endregion

  // #region [ METHODS ]
  private dateOnInput(unformattedDate: string) {
    let dateDMY;

    if (this.propMonth) {
      // Formats the YYYY-MM date to DD/MM/YYYY
      dateDMY = DateUtil.formatYYYYMMToDDMMYYYY(unformattedDate);
    } else {
      // Formats the YYYY-MM-DD date to DD/MM/YYYY
      dateDMY = DateUtil.formatToDDMMYYYY(unformattedDate);
    }

    this.internalValue = dateDMY;

    this.showMenu = false;
    this.dateWasSelected = true;

    this.emitInput();
  }

  private async fieldOnBlur() {
    if(this.multiple) return;

    if (this.internalValue !== '') {
      this.internalValue = this.validDate;
    }

    await Util.setTimeout(10);

    if (!this.dateWasSelected && this.changed) {
      this.emitInput();
    }
  }

  private fieldOnInput() {
    this.dateWasSelected = false;
  }

  private fieldOnTab() {
    this.showMenu = false;
  }

  private iconOnClick() {
    if (this.dateMode === this.dateViewMode) {
      // this.dateModes.viewMode.clearField();
      this.selectedDates = [];
      this.internalValue = '';
      this.dateMultipleChange();
      this.emitInput();
    }
  }
  
  private dateMultipleOnInput(date) {
    const dateDMY = DateUtil.formatToDDMMYYYY(date)

    if (this.selectedDates.includes(dateDMY)) {
      this.selectedDates = this.selectedDates.filter(d => d !== dateDMY);
    } else {
      this.selectedDates.push(dateDMY);
    }

    this.internalValue = this.selectedDates.join(', ');

    this.addDaysListeners();
    this.dateMultipleChange();

    this.emitInput();
  }

  private dateOnFocus(date) {
    this.selectedDates = this.internalValue ? this.internalValue.split(', ') : [];
    this.handleMonthClick();
    this.emitFocus();
  }

  private allowedDates(date) {
    // Aqui você pode definir regras para permitir ou desabilitar datas
    return true; // Permite todas as datas
  }

  private handleMonthClick = async () => {
    await this.sleep(this.timerCalendar);
    this.dateMultipleChange();
  };

  private addDaysListeners() {
    if (!this.$refs.date) return;

    const datePicker = (this.$refs.date as Vue).$el;
    const arrowMesButtons = datePicker.querySelectorAll('.v-picker--date .v-btn');

    if (arrowMesButtons.length && !this.isEventListenerAddedDay) {
      this.isEventListenerAddedDay = true;

      // Adicione o listener a cada botão encontrado
      arrowMesButtons.forEach(button => {
        button.addEventListener('click', (e: any) => {
          const isArrow = e.currentTarget.querySelector('i');
          if(isArrow) {
            this.isEventListenerAddedCurrentHeader = false;
            setTimeout(() => {
              this.addCurrentHeaderListener('month');
            }, this.timerCalendar);
          }

          this.handleMonthClick();
        });
      });

      this.addCurrentHeaderListener('month');
    }
  }

  private addCurrentHeaderListener(type: string) {
    const calendarHeader = document.querySelector('.v-date-picker-header__value .primary--text button');
    if(!calendarHeader) return;

    if(!this.isEventListenerAddedCurrentHeader) {
      this.isEventListenerAddedCurrentHeader = true;

      calendarHeader.addEventListener('click', () => {
        this.isEventListenerAddedMonth = false;
        this.isEventListenerAddedYear = false;
        setTimeout(() => {
          type == 'month' ? this.addMonthListeners() : this.addYearsListener();
        }, this.timerCalendar);
      });
    }
  }


  private addMonthListeners() {
    if (!this.$refs.date) return;

    const datePicker = (this.$refs.date as Vue).$el;
    const arrowMesButtons = datePicker.querySelectorAll('.v-picker--date .v-btn');

    if (arrowMesButtons.length && !this.isEventListenerAddedMonth) {
      this.isEventListenerAddedMonth = true;

      // Adicione o listener a cada botão encontrado
      arrowMesButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.isEventListenerAddedDay = false;
          this.isEventListenerAddedCurrentHeader = false;
          setTimeout(() => {
            this.handleMonthClick();
            this.addDaysListeners();
          }, this.timerCalendar);
        });
      });
      this.isEventListenerAddedCurrentHeader = false;
      this.addCurrentHeaderListener('year');
      //this.addYearsListener();
    }
  }


  private addYearsListener() {
    const calendarYear = document.querySelector('.v-date-picker-years');
    if(!calendarYear) return;

    if(!this.isEventListenerAddedYear) {
      this.isEventListenerAddedYear = true;

      calendarYear.addEventListener('click', () => {
        this.isEventListenerAddedMonth = false;
        this.isEventListenerAddedYear = false
        setTimeout(() => {
          this.addMonthListeners();
        }, this.timerCalendar);
      });
    }
  }

  private removeMonthListeners() {
    if (!this.$refs.date) return;

    const datePicker = (this.$refs.date as Vue).$el;
    const arrowMesButtons = datePicker.querySelectorAll('.v-picker--date .v-btn');

    if (arrowMesButtons.length) {
      arrowMesButtons.forEach(button => {
        button.removeEventListener('click', this.handleMonthClick);
      });
      this.isEventListenerAddedDay = false;
      this.isEventListenerAddedMonth = false;
    }
  }

  private dateMultipleChange() {
    if(!this.multiple) return;

    const calendarHeader = document.querySelector('.v-date-picker-header__value .primary--text button');

    if(!calendarHeader) return;

    const mesEAno = calendarHeader.textContent;

    const [monthName, year] = mesEAno.split(' de ');
    const month = this.getMonthNumber(monthName);

    const dataCalendario = parse(`${year}-${month}-01`, 'yyyy-M-d', new Date(), { locale: ptBR });

    const datasDoMes = this.selectedDates.filter((date: string) => {
      const dateObj = parse(date, 'dd/MM/yyyy', new Date(), { locale: ptBR });

      if(this.areDatesInSameMonthAndYear(dateObj, dataCalendario)) {
        return true;
      }

      return false;
    });

    const diasDoMes = datasDoMes.map(data => data.split('/')[0].replace(/^0/, ''));

    const diasCalendarioBtnElement = document.querySelectorAll('.v-date-picker-table .v-btn')

    diasCalendarioBtnElement.forEach((elementDia) => {

      const diaElement = elementDia.querySelector('.v-btn__content');
      const dia = diaElement.textContent;

      if (diasDoMes.includes(dia)) {
        elementDia.classList.add('selected-day');
      } else {
        elementDia.classList.remove('selected-day');
      }

    });
  }

  // Função para converter o nome do mês para o número do mês
  private getMonthNumber(monthName) {
    const months = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    return months.indexOf(monthName.toLowerCase()) + 1;
  }

  private areDatesInSameMonthAndYear (date1: Date, date2: Date)  {
    return getYear(date1) === getYear(date2) && getMonth(date1) === getMonth(date2);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private __convertDMYToISO(dateStr: string): string | null {
    if (!dateStr) return null;

    // If date is in the ISO 8601 format (YYYY-MM-DD)
    if (dateStr.includes('-')) {
      return dateStr;
    }

    return DateUtil.formatToYYYYMMDD(dateStr);
  }
  // #endregion
}

export default TkDate;
